// Backend web PRIMARIO: @sqlite.org/sqlite-wasm opfs-sahpool corriendo en un Worker.
// Implementa el contrato WebSqliteBackend (ver WebSqliteBackend.js).
//
// sahpool es Worker-ONLY: este archivo vive en el main thread y habla con
// sqlite.worker.js por un puente de promesas correlacionadas (contador monotono de id
// + mapa de promesas pendientes). query() devuelve { values } ya normalizado por el worker.
//
// persist() es no-op: OPFS escribe al filesystem al vuelo (sin saveToStore).

// Cota dura para CADA op del worker. Si el worker no responde (kill por presión de memoria en móvil —
// NO dispara onerror; o un cuelgue del VFS), la promesa se rechaza y el worker se recicla en vez de
// quedar pendiente para SIEMPRE: como _post pasa por _withWriteLock, un cuelgue congelaría TODAS las
// escrituras de la sesión sin error visible. Generoso (open instala el VFS, executeSet puede ser grande).
const POST_TIMEOUT_MS = 60000

export class OpfsSahPoolBackend {
  constructor () {
    this.name = 'opfs-sahpool'
    this._worker = null
    this._seq = 0
    this._pending = new Map()
    // Asignado por selectBackend.js tras un open() exitoso (Web Lock multi-pestaña); null si esta
    // instancia se creó/abrió fuera de selectWebBackend (p.ej. tests).
    this._releaseOpfsLock = null
  }

  /** Rechaza TODO lo pendiente (limpiando sus timers) con el error dado. */
  _failAllPending (e) {
    for (const [, p] of this._pending) {
      if (p.timer) clearTimeout(p.timer)
      p.reject(e)
    }
    this._pending.clear()
  }

  /** Termina + nullea el worker para que el próximo _post/reopen lo re-cree (worker muerto/colgado). */
  _recycleWorker () {
    const w = this._worker
    this._worker = null
    if (w) { try { w.terminate() } catch { /* noop */ } }
  }

  /**
   * Instancia el worker (patron nativo Webpack 5), engancha handlers, y ABRE LA DB (handshake 'open').
   * Idempotente: si el worker ya vive, no-op. La recursión con _post está cortada por el guard
   * `if (this._worker) return` — el worker se asigna ANTES del handshake, así que el _post('open') de
   * abajo re-entra a _ensureWorker y sale por el guard sin re-crear.
   */
  async _ensureWorker () {
    if (this._worker) return
    const worker = new Worker(new URL('./sqlite.worker.js', import.meta.url), { type: 'module' })
    this._worker = worker
    this._worker.onmessage = (e) => {
      const { id, ok, result, error } = e.data || {}
      const p = this._pending.get(id)
      if (!p) return
      this._pending.delete(id)
      if (p.timer) clearTimeout(p.timer)
      if (ok) p.resolve(result)
      else p.reject(new Error('[offline-v2][opfs-sahpool] ' + error))
    }
    this._worker.onerror = (err) => {
      // Rechaza todo lo pendiente Y RECICLA el worker: sin terminate+null, los _post siguientes se
      // encolarían hacia un worker muerto y colgarían para siempre (y reopen vería _worker no-null). [review Hugo]
      const e = new Error('[offline-v2][opfs-sahpool] worker error: ' + (err && err.message ? err.message : String(err)))
      this._failAllPending(e)
      this._recycleWorker()
    }
    // Handshake OBLIGATORIO [review Hugo #4]: un worker recién creado (arranque O RECICLADO tras
    // timeout/onerror) tiene la DB SIN abrir; el VFS/handle solo se instalan con 'open'. Sin esto, tras
    // un reciclado (executeSet grande venció los 60s en gama baja) TODO query/run fallaba el RESTO de la
    // sesión hasta un background->foreground. Ahora el próximo _post re-crea el worker Y reabre la DB.
    try {
      await this._post('open', {})
    } catch (e) {
      // El handshake puede fallar por RESPUESTA {ok:false} (installOpfsSAHPoolVfs lanzó dentro del worker):
      // ese camino NO pasa por onerror NI por el timeout — los únicos que reciclan. Sin este catch, _worker
      // quedaría no-null con la DB SIN abrir, el guard de arriba saltaría la reapertura para siempre, y todo
      // _post posterior moriría con "DB no abierta" el RESTO de la sesión. Reciclamos para que el próximo
      // _post re-cree el worker y reabra. [review Hugo #6]
      // El guard `=== worker` evita matar un worker AJENO ya re-creado (el timeout/onerror pudo reciclar
      // este mismo worker antes de que su rechazo llegara hasta aquí).
      if (this._worker === worker) {
        // Ops encoladas a este worker mientras el handshake seguía en vuelo (el guard las dejó pasar):
        // van a un worker que estamos por terminar. Se rechazan YA en vez de colgar hasta su timeout,
        // igual que hace onerror.
        this._failAllPending(e)
        this._recycleWorker()
      }
      throw e
    }
  }

  async _post (op, args) {
    await this._ensureWorker()
    const id = ++this._seq
    const worker = this._worker
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (!this._pending.has(id)) return
        this._pending.delete(id)
        reject(new Error(`[offline-v2][opfs-sahpool] timeout (${POST_TIMEOUT_MS}ms) op=${op}`))
        // Recicla el worker (si sigue siendo el mismo) para que reopenIfClosed/el próximo _post sanen.
        if (this._worker === worker) this._recycleWorker()
      }, POST_TIMEOUT_MS)
      this._pending.set(id, { resolve, reject, timer })
      this._worker.postMessage({ id, op, args })
    })
  }

  async open () {
    // _ensureWorker ya hace el handshake 'open'; delegamos (no-op si el worker ya vive).
    await this._ensureWorker()
  }

  /** SIEMPRE { values: [objetos] } (el worker ya normaliza). */
  async query (sql, params) {
    return this._post('query', { sql, params })
  }

  async run (sql, params) {
    return this._post('run', { sql, params })
  }

  async execute (statements) {
    return this._post('execute', { sql: statements })
  }

  /** Batch atomico cero-perdida: una sola ida/vuelta, transaccion en el worker. */
  async executeSet (set) {
    return this._post('executeSet', { set })
  }

  /**
   * Transaccion multi-paso bridge: ejecuta fn contra un proxy delgado cuyas escrituras
   * van al worker. Para el camino atomico de F2 se prefiere executeSet (un round-trip).
   * Aqui envolvemos en BEGIN/COMMIT explicito por el worker.
   */
  async transaction (fn) {
    await this._post('run', { sql: 'BEGIN' })
    try {
      const result = await fn(this)
      await this._post('run', { sql: 'COMMIT' })
      return result
    } catch (error) {
      try { await this._post('run', { sql: 'ROLLBACK' }) } catch { /* noop */ }
      throw error
    }
  }

  /** Seam forward-compat no-op (Sonar S1186 = falso positivo intencional): OPFS escribe al filesystem al vuelo, sin saveToStore. [review Hugo #14] */
  async persist () {}

  /**
   * Seam de mitigacion iOS handle-close (research Pitfall 5): tras background ->
   * foreground el sync access handle de OPFS puede quedar invalidado en WKWebView.
   * Re-abre idempotentemente el handle de la DB en el worker. Si el worker MISMO
   * murio (onerror limpio _worker), lo re-crea via open(). Para wa-sqlite/sql.js
   * el handle no se cierra igual; ahi reopen es practicamente no-op (ver sus clases).
   */
  async reopen () {
    if (!this._worker) {
      // Worker perdido: re-crear desde cero (open instala VFS + abre DB).
      await this.open()
      return
    }
    try {
      await this._post('reopen', {})
    } catch (e) {
      console.warn('[offline-v2][opfs-sahpool] reopen fallo, re-creando worker:', String(e && e.message ? e.message : e))
      try { this._worker.terminate() } catch { /* noop */ }
      this._worker = null
      await this.open()
    }
  }

  async close () {
    if (!this._worker) { this._releaseOpfsLock?.(); return }
    try { await this._post('close', {}) } catch { /* noop */ }
    this._worker.terminate()
    this._worker = null
    // Libera el Web Lock multi-pestaña que selectBackend.js asignó en `_releaseOpfsLock` al abrir con
    // éxito (no-op si nunca se asignó, p.ej. instancias creadas fuera de selectWebBackend).
    this._releaseOpfsLock?.()
  }
}
